/**
 * Utility per la pulizia completa dello storage
 * Utile per risolvere problemi di autenticazione persistenti
 */

/**
 * Controlla se è presente un parametro di forza logout nell'URL
 * @returns true se è presente il parametro force_logout=true
 */
export function checkForceLogoutParam(): boolean {
  return window.location.search.includes('force_logout=true');
}

/**
 * Inizializza le funzioni di pulizia storage (può essere chiamato all'avvio)
 */
export function initStorageClear(): void {
  console.log('Inizializzazione sistema di pulizia storage...');
  
  // Verifica se è necessario eseguire una pulizia all'avvio
  if (checkForceLogoutParam()) {
    console.log('Rilevata richiesta di logout forzato, pulizia storage in corso...');
    clearAllStorage(true);
  }
  
  console.log('Sistema di pulizia storage inizializzato');
}

/**
 * Funzione per eliminare tutti i dati di autenticazione dal browser
 * @param deep Se true, esegue una pulizia più approfondita che include tutte le 
 * preferenze dell'utente (non solo l'autenticazione)
 */
export function clearAllStorage(deep: boolean = false): void {
  console.log(`Esecuzione pulizia storage ${deep ? 'completa' : 'base'}...`);
  
  // Elimina il token JWT
  try {
    localStorage.removeItem('auth_token');
    console.log('- Token JWT eliminato');
  } catch (e) {
    console.error('Errore nella rimozione token JWT:', e);
  }
  
  // Elimina i cookie di autenticazione
  try {
    document.cookie = 'AUTH_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'connect.sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    console.log('- Cookie di autenticazione eliminati');
  } catch (e) {
    console.error('Errore nella rimozione cookie:', e);
  }
  
  // Cancella sessionStorage di autenticazione
  try {
    sessionStorage.removeItem('auth_token');
    console.log('- Token JWT da sessionStorage eliminato');
  } catch (e) {
    console.error('Errore nella rimozione token da sessionStorage:', e);
  }

  // Se richiesta pulizia completa, elimina tutto localStorage e sessionStorage
  if (deep) {
    try {
      // Backup degli elementi che vogliamo mantenere (personalizzabile)
      const keepItems: Record<string, string> = {};
      // Ad esempio per mantenere le preferenze di tema:
      // const themePreference = localStorage.getItem('theme-preference');
      // if (themePreference) keepItems['theme-preference'] = themePreference;
      
      // Pulisci tutto lo storage
      localStorage.clear();
      sessionStorage.clear();
      console.log('- Storage completo eliminato');
      
      // Ripristina gli elementi da mantenere
      Object.entries(keepItems).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
    } catch (e) {
      console.error('Errore nella pulizia completa dello storage:', e);
    }
  }
  
  console.log('Pulizia storage completata');
}

/**
 * Funzione per verificare lo stato dell'autenticazione attuale
 * Restituisce un oggetto con i dati trovati nei vari storage
 */
export function getAuthDebugInfo(): Record<string, any> {
  const info: Record<string, any> = {};
  
  // Controlla localStorage
  try {
    info.localStorageToken = localStorage.getItem('auth_token') ? 
      localStorage.getItem('auth_token')?.substring(0, 20) + '...' : 
      null;
  } catch (e) {
    info.localStorageError = e instanceof Error ? e.message : 'Errore sconosciuto';
  }
  
  // Controlla sessionStorage
  try {
    info.sessionStorageToken = sessionStorage.getItem('auth_token') ?
      sessionStorage.getItem('auth_token')?.substring(0, 20) + '...' :
      null;
  } catch (e) {
    info.sessionStorageError = e instanceof Error ? e.message : 'Errore sconosciuto';
  }
  
  // Controlla cookie
  try {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>);
    
    info.cookies = {
      AUTH_TOKEN: cookies.AUTH_TOKEN ? cookies.AUTH_TOKEN.substring(0, 20) + '...' : null,
      'connect.sid': cookies['connect.sid'] ? cookies['connect.sid'].substring(0, 20) + '...' : null
    };
  } catch (e) {
    info.cookiesError = e instanceof Error ? e.message : 'Errore sconosciuto';
  }
  
  return info;
}